'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * Labs progress indicator — the SOURCE vanilla prototype's CLEAR design (header `#progress-track` /
 * `#progress-line` / `#progress-step`), rendered through the Extern finance-sprint design system
 * (fs-* tokens; `fs-` names the DESIGN SYSTEM, not the finance vertical).
 *
 * SOURCE design (clearer than a label-less full-bleed bar):
 *   - a COMPACT rounded pill track (`w-28 sm:w-40`, `h-2.5`, `rounded-full`), NOT a full-width band;
 *   - a brand-colored fill with an animated width;
 *   - a LIVE "42%" label next to it (`text-xs font-semibold tabular-nums`, brand color).
 * The percentage label is the main clarity win — it shows the actual number, not just a bar.
 *
 * Token mapping (source intent → fs-* token):
 *   - track #D8D5CC + ring-black/5        → `bg-fs-surface-subtle` + `ring-fs-border`
 *   - fill  brand #0F4D3A                  → `bg-fs-primary`
 *   - "42%" label brand #0F4D3A            → `text-fs-primary`
 *
 * This component knows NOTHING about the flow: it renders whatever `current`/`total`/`fraction` it
 * is handed. <StepShell> owns the progress MODEL (which steps move the bar and by how much) and
 * feeds this the module counts — see its doc comment.
 *
 * `fraction` (0..1) is an optional INTRA-STEP override for screens that hold several activities
 * inside one step (e.g. the M1 swipe deck's cards). When supplied it advances the fill smoothly
 * BETWEEN the current step and the next, so the bar visibly moves on every completed activity
 * instead of jumping only at the module boundary. Omit it for plain screens.
 *
 * Designed to sit in the top-bar header row. The welcome screen does NOT render this.
 */
export function ProgressBar(t0) {
  const $ = _c(15);
  const {
    current,
    total,
    fraction,
    className
  } = t0;
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 0), safeTotal);
  const f = fraction === undefined ? 0 : Math.min(1, Math.max(0, fraction));
  const stepsCompleted = safeCurrent + f;
  const pct = Math.min(100, Math.max(0, stepsCompleted / safeTotal * 100));
  let t1;
  if ($[0] !== pct) {
    t1 = Math.round(pct);
    $[0] = pct;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pctLabel = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("flex items-center gap-2", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${pct}%`;
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="h-full rounded-fs-pill bg-fs-primary transition-[width] duration-300 ease-out" style={{
      width: t3
    }} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== pctLabel || $[7] !== t4) {
    t5 = <div aria-label="Progress" aria-valuemax={100} aria-valuemin={0} aria-valuenow={pctLabel} className="h-2.5 w-28 overflow-hidden rounded-fs-pill bg-fs-surface-subtle ring-1 ring-fs-border sm:w-40" role="progressbar">{t4}</div>;
    $[6] = pctLabel;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== pctLabel) {
    t6 = <span aria-hidden={true} className="font-semibold text-fs-primary text-xs tabular-nums" role="status">{pctLabel}%</span>;
    $[9] = pctLabel;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className={t2}>{t5}{t6}</div>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}